export type RGB = [number, number, number];
export type HSL = [number, number, number];

export const convertHexToSixDigit = (hex: string): string => {
    return hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
};

const hexToRGB = (hex: string): RGB => {
    hex = convertHexToSixDigit(hex);
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return [r, g, b];
};

export function hexToHSL(hex: string): HSL {
    // Convert hex to RGB first
    // credit https://codesandbox.io/s/2zd4x?file=/src/index.js:169-1060

    let [r, g, b] = hexToRGB(convertHexToSixDigit(hex));
    // Then to HSL
    r /= 255;
    g /= 255;
    b /= 255;
    const cmin = Math.min(r, g, b),
        cmax = Math.max(r, g, b),
        delta = cmax - cmin;
    let h = 0,
        s = 0,
        l = 0;

    if (delta !== 0) {
        if (cmax === r) h = ((g - b) / delta) % 6;
        else if (cmax === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
    }
    h = Math.round(h * 60);

    if (h < 0) h += 360;

    l = (cmax + cmin) / 2;
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(0);
    l = +(l * 100).toFixed(0);

    return [h, s, l];
}

export function hslToHex(h: number, s: number, l: number) {
    h /= 360;
    s /= 100;
    l /= 100;
    let r: number, g: number, b: number;
    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (x: number) => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export enum LightOrDark {
    Light,
    Dark,
}

export const getSuggestedForegroundColor = (hex: string): LightOrDark => {
    const [r, g, b] = hexToRGB(hex);
    const brightness = Math.round((r * 299 + g * 587 + b * 114) / 1000);
    return brightness > 125 ? LightOrDark.Dark : LightOrDark.Light;
};

const luminance = (rgb: RGB) => {
    const [r, g, b] = rgb.map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return r * 0.2126 + g * 0.7152 + b * 0.0722;
};

export const computeContrast = (foregroundColor: string, backgroundColor: string): number => {
    if (!foregroundColor || !backgroundColor) {
        return 0;
    }
    const foregroundLumiance = luminance(hexToRGB(foregroundColor));
    const backgroundLuminance = luminance(hexToRGB(backgroundColor));
    return (
        -1 /
        (-1 *
            (backgroundLuminance < foregroundLumiance
                ? (backgroundLuminance + 0.05) / (foregroundLumiance + 0.05)
                : (foregroundLumiance + 0.05) / (backgroundLuminance + 0.05)))
    );
};
